import { vm, newServiceWorker, sharedData } from '@/main'
import Vue from 'vue'
import { partnerData } from './partner'
import { getFuelPercentage } from '@/utils/positions'
import { reducePositions } from '@/utils/index'

export function getDate(dateString) {
  return Vue.moment(dateString)
}

export function formatDate(v) {
  v = sharedData.getPositions().findIndex(x => Vue.moment(x.fixTime).unix() === v)
  const fixTime = sharedData.getPositions()[v] ? sharedData.getPositions()[v].fixTime : new Date()
  const speed = sharedData.getPositions()[v] ? sharedData.getPositions()[v].speed : ''
  let result = getDate(fixTime).format('YYYY-MM-DD HH:mm:ss')
  if (speed && speed > 0) {
    result += (' ' + ~~(speed * 1.852) + 'km/h')
  }
  return result
}

export function formatAddress(v) {
  return (sharedData.getPositions() && sharedData.getPositions()[v]) ? sharedData.getPositions()[v].address : ''
}

export function calculateTimeHHMM(idleSeconds) {
  if (idleSeconds < 0) return '00:00'

  const idleHours = String(Math.floor(idleSeconds / 3600)).padStart(2, '0')
  idleSeconds %= 3600
  const idleMinutes = String(Math.floor(idleSeconds / 60)).padStart(2, '0')
  return idleHours + ':' + idleMinutes
}

export function calculateTimeHHMMSS(idleSeconds) {
  const idleHours = String(Math.floor(idleSeconds / 3600)).padStart(2, '0')
  idleSeconds %= 3600
  const idleMinutes = String(Math.floor(idleSeconds / 60)).padStart(2, '0')
  idleSeconds %= 60
  return idleHours + ':' + idleMinutes + ':' + String(idleSeconds).padStart(2, '0')
}

export function filterPositions(positions) {
  positions.sort((a, b) => new Date(a.fixTime) - new Date(b.fixTime))
  const firstPos = positions.findIndex(position =>
    position.attributes.ignition || position.attributes.motion || position.attributes.putAccumulators)
  if (firstPos > 0) {
    Vue.$log.debug('slicing positions at ', firstPos)
    positions = positions.slice(firstPos)
  }
  return reducePositions(positions.filter(filterPosition))
}

function filterPosition(p) {
  if (p.attributes.putAccumulators) {
    return true
  }
  if (p.valid === false) {
    if (p.protocol === 'osmand' && p.attributes.event === 12) {
      return true
    }
    return p.attributes.ignition || p.attributes.motion
  }
  if (p.protocol === 'osmand') {
    return !(p.attributes.event >= 200 || p.attributes.event === 30)
  }
  return true
}

export function reload() {
  if (newServiceWorker) {
    Vue.$log.info(newServiceWorker, 'skipWaiting!')
    newServiceWorker.postMessage({ action: 'skipWaiting' })
    window.location.reload()
  } else {
    Vue.$log.error(`this shouldn't happen`)
  }
  Vue.$log.info('bye bye...')
}

export function checkForUpdates() {
  if (navigator.serviceWorker) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      if (registrations.length === 0) {
        Vue.$log.warn('no service worker registrations... thats not good...')
      }
      for (const reg of registrations) {
        reg.update().then(() => Vue.$log.warn('done checking for updates...')).catch(e => console.error(e))
      }
    })
  } else {
    Vue.$log.warn('no serviceWorker, weird browser...')
  }
}

export function appOffline() {
  return !vm.$store.state.socket.isConnected || !vm.$store.state.socket.connectionOk
}

export function getDeviceColor(state) {
  switch (state) {
    case 'Disconnected': return 'gray'
    case 'Moving': return 'green'
    case 'Idle': return 'yellow'
    case 'Stopped': return 'red'
  }
}

const minDaysForDisconnected = partnerData().minDaysForDisconnected || 5
export function getDeviceState(position) {
  if (!position || position.fixDays > minDaysForDisconnected || (new Date() - new Date(position.fixTime)) / (1000 * 60 * 60 * 24) > minDaysForDisconnected) {
    return 'Disconnected'
  } else if (position.speed >= 4 && position.attributes.ignition !== false) {
    return 'Moving'
  } else if (!position.attributes.ignition) {
    return 'Stopped'
  } return 'Idle'
}

export function calculateFuelLevel(adc1CacheValues, position, lastPosition, device) {
  if ('fuel' in position.attributes) {
    if ((position.protocol === 'teltonika' && !position.attributes.io6) || position.protocol === 'gl200') {
      position.attributes.fuelLevel = getFuelPercentage(device, position)
    } else if (device.attributes.xpert) {
      // DEVICE WITH XPERT
      position.attributes.fuelLevel = Math.round(position.attributes.fuel)
    } else {
      if ('fuel_low_threshold' in device.attributes &&
        'fuel_high_threshold' in device.attributes) {
        // DEVICE WITH FUEL SENSOR
        if (adc1CacheValues.length === 5) {
          adc1CacheValues.splice(0, 1)
        }

        adc1CacheValues.push(position.attributes.fuel)

        const adc1CalculatedValue = (adc1CacheValues.reduce((total, value) => total + value, 0)) / adc1CacheValues.length
        const level = Math.round(((device.attributes.fuel_low_threshold - adc1CalculatedValue) / (device.attributes.fuel_low_threshold - device.attributes.fuel_high_threshold)) * 100)

        if (level >= 0 && level <= 100) {
          position.attributes.fuelLevel = level
          position.adc1CacheValues = adc1CacheValues
        } else if (lastPosition) {
          position.attributes.fuelLevel = lastPosition.attributes.fuelLevel
          position.adc1CacheValues = adc1CacheValues
        }
      } else {
        position.attributes.fuelLevel = position.attributes.fuel
      }
    }
  }
}

export function calculateFuelConsumption(t, stop, positions, device, index) {
  let fuelConsumption = (t.spentFuel > 0 ? t.spentFuel : 0) + (stop && stop.spentFuel > 0 ? stop.spentFuel : 0)

  if (device.attributes.xpert) {
    const fuelUsed = positions.find(p => p.attributes.fuelUsed)
    if (!fuelUsed) {
      if (fuelConsumption > 0) {
        fuelConsumption = (fuelConsumption * device.attributes.fuel_tank_capacity) / 100
      } else {
        const endTime = new Date(t.endTime).getTime()
        const xpertlocations = positions.filter(p => {
          const pDate = new Date(p.fixTime).getTime()
          return (index === 0 || pDate >= new Date(t.startTime).getTime()) &&
            (stop ? pDate <= new Date(stop.endTime).getTime() : pDate <= endTime)
        })
        const xpertMessages = xpertlocations.map(p => p.attributes.xpert).flat().filter(p => p)
        const xpertEndTripMessage = xpertMessages.filter(x => x.type === '3')
        if (xpertEndTripMessage.length > 1) {
          const diff = xpertEndTripMessage[xpertEndTripMessage.length - 1].total_fuel - xpertEndTripMessage[0].total_fuel
          fuelConsumption = Math.round(diff)
        }
      }
      return fuelConsumption
    }
  }

  return fuelConsumption
}

export function calculateTotalFuelFromSensor(trips, stops, positions, device, firstStopIndex) {
  const lastStopIndex = firstStopIndex + trips.length - 1
  const lastStop = stops.length > lastStopIndex ? stops[lastStopIndex] : null
  const firstTripStart = new Date(trips[0].startTime).getTime()
  const lastEndTime = lastStop ? new Date(lastStop.endTime).getTime() : new Date(trips[trips.length - 1].endTime).getTime()

  const rangePositions = positions.filter(p => {
    const t = new Date(p.fixTime).getTime()
    return t >= firstTripStart && t <= lastEndTime && p.attributes.fuel != null
  })

  if (rangePositions.length < 2) return 0

  const invertedSensor = device.attributes.fuel_low_threshold > device.attributes.fuel_high_threshold
  const sensorRange = Math.abs(device.attributes.fuel_high_threshold - device.attributes.fuel_low_threshold)
  const refuelThreshold = sensorRange * 0.2

  let totalRawConsumption = 0
  let segmentStartFuel = rangePositions[0].attributes.fuel

  for (let i = 1; i < rangePositions.length; i++) {
    const prev = rangePositions[i - 1]
    const curr = rangePositions[i]
    // refuel = significant increase for normal sensor, significant decrease for inverted
    const fuelChange = invertedSensor
      ? prev.attributes.fuel - curr.attributes.fuel
      : curr.attributes.fuel - prev.attributes.fuel

    if (fuelChange > refuelThreshold) {
      const segmentRawDiff = invertedSensor
        ? prev.attributes.fuel - segmentStartFuel
        : segmentStartFuel - prev.attributes.fuel
      if (segmentRawDiff > 0) totalRawConsumption += segmentRawDiff
      segmentStartFuel = curr.attributes.fuel
    }
  }

  const lastFuel = rangePositions[rangePositions.length - 1].attributes.fuel
  const lastSegmentRawDiff = invertedSensor
    ? lastFuel - segmentStartFuel
    : segmentStartFuel - lastFuel
  if (lastSegmentRawDiff > 0) totalRawConsumption += lastSegmentRawDiff

  if (totalRawConsumption <= 0) return 0
  return (totalRawConsumption / sensorRange) * device.attributes.fuel_tank_capacity
}

export function chunkArray(originalArray, chunkSize) {
  const results = []
  const newArray = [...originalArray]

  while (newArray.length) {
    results.push(newArray.splice(0, chunkSize))
  }

  return results
}

export function getEventDescription(event) {
  const result = (vm.$t('settings.alert_' + event.type) !== 'settings.alert_' + event.type)
    ? vm.$t('settings.alert_' + event.type)
    : event.type
  const info = event.info || (event.attributes && event.attributes.info)
  return result + (info ? ` ${info}` : '')
}
